import React, {useEffect, useRef, useState} from 'react';
import WishlistItem from './WishlistItem.jsx';
import MessageBroker from 'deprecated-shared-ui/components/message-broker';
import WishlistMessages from '@shopping-funnel/scripts/components/messages';
import Messages from '@shopping-funnel/scripts/components/messages';
import WishlistEmptylist from './WishlistEmptylist.jsx';
import meta from '@core/scripts/helper/meta';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import {compareWishlistItems} from '@wishlist/components/sort-and-filter/sorting-helper';
import WishlistSorting from '@wishlist/components/sort-and-filter/WishlistSorting.jsx';
import {Grid} from '@bonprix/design-system';
import setFocusAnchor from '@wishlist/helper/focusAnchor.js';
import SimilarArticlesSheet from '@shopping-funnel/scripts/components/similar-articles/SimilarArticlesSheet';
import {useAppSortButton} from "@core/scripts/modules/shoppingApp/appSortButton.ts";

const FADEOUT_TIME = 0.5;
const ITEM_CHANGING_OPACITY = 0.3;

export default function WishlistItemContainer({
                                                  pointOfSaleId,
                                                  initialWishlistSortOption,
                                                  wishlistItems,
                                                  setWishlistItems,
                                                  texts,
                                                  userIsLoggedIn
                                              }) {

    const [fadingItems, setFadingItems] = useState([]);
    const lastSorting = useRef(initialWishlistSortOption);
    const [selectedWishlistItem, setSelectedWishlistItem] = useState(null);
    const [similarArticlesSheetOpen, setSimilarArticlesSheetOpen] = useState(false);

    const resortWishlistItems = (sortingName) => {
        setWishlistItems(previousItems => {
            let newItemSorting = structuredClone(previousItems);
            newItemSorting.sort((a, b) => compareWishlistItems(a, b, sortingName));
            return newItemSorting;
        });
    };

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(WishlistMessages.WISHLIST.ITEM_DELETED, (wishlistRemovalPayload) => {
                setFadingItems((previousItems) => {
                    const newDeletedItems = Object.assign([], previousItems);
                    const itemIndex = wishlistItems.findIndex(
                        (item) =>
                            item.digistyleId === wishlistRemovalPayload.digistyleId &&
                            (!wishlistRemovalPayload.itemId || item.itemId === wishlistRemovalPayload.itemId)
                    );
                    if (itemIndex !== -1) {
                        newDeletedItems.push({
                            id: wishlistRemovalPayload.digistyleId,
                            itemId: wishlistRemovalPayload.itemId,
                            deletionTime: new Date(),
                            index: itemIndex
                        });
                        const wishlistItemElement = document.querySelector('.wishlist__productlist .grid')?.children[itemIndex]?.querySelector('.wishlist-item');
                        if (wishlistItemElement) {
                            setFocusAnchor(wishlistItemElement);
                        }
                    }
                    return newDeletedItems;
                });
            }),

            MessageBroker.subscribe(WishlistMessages.ARTICLE.PROPERTIES_UPDATED, (messageData) => {
                setFadingItems((previousItems) => {
                    const newDeletedItems = Object.assign([], previousItems);
                    newDeletedItems.push({
                        id: messageData.digistyleId,
                        previousItemId: messageData.previousItemId,
                        deletionTime: -1
                    });
                    return newDeletedItems;
                });
                if (messageData.success) {
                    let additionalParameters = Boolean(messageData.newItemId) ? `&itemId=${messageData.newItemId}` : '';
                    additionalParameters += Boolean(messageData.newItemOptionId)
                        ? `&itemOptionId=${messageData.newItemOptionId}`
                        : '';
                    fetchWithCSRF(
                        `${meta.value('bonprix:baseUrl')}list/wishlist/refreshItem/?digistyleId=${
                            messageData.digistyleId
                        }${additionalParameters}`,
                        {
                            method: 'GET'
                        }
                    ).then((response) => {
                        if (response.ok) {
                            response.json().then((data) => {
                                setWishlistItems((previousItems) => {
                                    const oldItems = Object.assign([], previousItems);
                                    return oldItems.map((item) => {
                                        const isUpdatedItem =
                                            item.digistyleId === data.digistyleId &&
                                            data.itemId === messageData.newItemId &&
                                            (messageData.previousItemId !== messageData.newItemId ||
                                                item.itemId === messageData.newItemId) &&
                                            messageData.previousItemId === item.itemId;
                                        if (isUpdatedItem) {
                                            return data;
                                        }
                                        return item;
                                    });
                                });
                                setFadingItems((previousItems) => {
                                    return Object.assign(
                                        [],
                                        previousItems.filter((item) => item.id !== messageData.digistyleId)
                                    );
                                });
                            });
                        }
                    });
                }
            }),

            MessageBroker.subscribe(WishlistMessages.WISHLIST.ITEM_ADDED, (messageData) => {
                fetchWithCSRF(
                    `${meta.value('bonprix:baseUrl')}list/wishlist/refreshItem/?digistyleId=${messageData.digistyleId}`,
                    {
                        method: 'GET'
                    }
                ).then((response) => {
                    if (response.ok) {
                        response.json().then((data) => {
                            setWishlistItems((previousItems) => {
                                let newItems = Object.assign([], previousItems);
                                newItems.unshift(data);
                                newItems.sort((a, b) => compareWishlistItems(a, b, lastSorting.current));
                                return newItems;
                            });
                            MessageBroker.publish(Messages.WISHLIST.ITEM_COUNT_CHANGED, {newCount: wishlistItems.length});
                        });
                    }
                });
            }),

            MessageBroker.subscribe(WishlistMessages.WISHLIST.SORTING_CHANGE, (messageData) => {
                lastSorting.current = messageData.sortingName;
                resortWishlistItems(messageData.sortingName);
            })
        ];
        return () => subscriptions.forEach((subscription) => MessageBroker.unsubscribe(subscription));
    }, [wishlistItems]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (fadingItems.length === 0) return;
            const itemsToDelete = [];
            for (let deletedItem of fadingItems) {
                if (deletedItem.deletionTime < 0) {
                    // skip deletion of item only meant to be faded
                    continue;
                }
                const deletedSince = (new Date() - deletedItem.deletionTime) / 1000;
                if (deletedSince >= FADEOUT_TIME) {
                    itemsToDelete.push(itemsToDelete.push({id: deletedItem.id, itemId: deletedItem.itemId}));
                }
            }
            setWishlistItems((previousItems) => {
                let remainingItems = Object.assign([], previousItems);
                remainingItems = remainingItems.filter(
                    (item) =>
                        !itemsToDelete.some(
                            (itemToDelete) =>
                                item.digistyleId === itemToDelete.id &&
                                (!itemToDelete.itemId || item.itemId === itemToDelete.itemId)
                        )
                );
                return remainingItems;
            });
            setFadingItems((currentlyFadingItems) => {
                let remainingFadingItems = Object.assign([], currentlyFadingItems);
                remainingFadingItems = remainingFadingItems.filter(
                    (item) =>
                        !itemsToDelete.some(
                            (itemToDelete) =>
                                item.id === itemToDelete.id &&
                                (!itemToDelete.itemId || item.itemId === itemToDelete.itemId)
                        )
                );
                return remainingFadingItems;
            });
        }, 20);
        return () => clearInterval(interval);
    }, [fadingItems, wishlistItems]);

    const wishlistIsEmpty = !Array.isArray(wishlistItems) || wishlistItems.length === 0;
    const appSortButton = useAppSortButton(!wishlistIsEmpty);

    if (wishlistIsEmpty) {
        return <WishlistEmptylist texts={texts} userIsLoggedIn={userIsLoggedIn}/>;
    }

    let itemList = wishlistItems.map((wishlistItem, itemIndex) => {
        let itemOpacity = 1.0;
        for (let potentiallyDeletedItem of fadingItems) {
            if (potentiallyDeletedItem.id === wishlistItem.digistyleId && potentiallyDeletedItem.deletionTime < 0) {
                if (potentiallyDeletedItem.previousItemId === wishlistItem.itemId) {
                    itemOpacity = ITEM_CHANGING_OPACITY;
                }
            } else if (
                potentiallyDeletedItem.id === wishlistItem.digistyleId &&
                (!potentiallyDeletedItem.itemId || potentiallyDeletedItem.itemId === wishlistItem.itemId)
            ) {
                itemOpacity = (FADEOUT_TIME - (new Date() - potentiallyDeletedItem.deletionTime) / 1000) / FADEOUT_TIME;
            }
        }
        return (
            <Grid.Item s={12} m={12} l={4} xl={3} key={wishlistItem.itemId}>
                <WishlistItem
                    item={wishlistItem}
                    texts={texts}
                    opacity={itemOpacity}
                    pointOfSaleId={pointOfSaleId}
                    setSimilarArticlesSheetOpen={setSimilarArticlesSheetOpen}
                    setSelectedWishlistItem={setSelectedWishlistItem}
                />
            </Grid.Item>
        );
    });

    return (
        <div className="wishlist__container">
            <Grid disableGutters={true}>
                <Grid.Item s={0} m={0} l={8} xl={9}/>
                <Grid.Item s={12} m={12} l={4} xl={3}>
                    <div className="wishlist__sorting-container">
                        <WishlistSorting
                            texts={texts}
                            preselectedOption={initialWishlistSortOption}
                            appSortButton={appSortButton}
                        />
                    </div>
                </Grid.Item>
            </Grid>
            <div className={'wishlist__productlist'}>
                <Grid disableGutters={true}>
                    {itemList}
                </Grid>
            </div>
            {similarArticlesSheetOpen && (
                <SimilarArticlesSheet
                    shopId={selectedWishlistItem.shopId}
                    texts={texts}
                    itemId={selectedWishlistItem.itemId}
                    itemNumber={selectedWishlistItem.orderNumber}
                    similarArticlesSheetOpen={similarArticlesSheetOpen}
                    setSimilarArticlesSheetOpen={setSimilarArticlesSheetOpen}
                    digistyleId={selectedWishlistItem.digistyleId}
                    assetId={selectedWishlistItem.assetId}
                    page={"wishlist"}
                />
            )}
        </div>
    );
}
